import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuthStore } from "@/store/useAuthStore";
import { matchingService } from "@/services/matchingService";
import { SHARE_CODE_LENGTH, WEB_AUTH_UI_ENABLED } from "@/config/publicConfig";
import { useTranslation } from "react-i18next";
import SeoHead from "@/components/SeoHead";

const InvitePage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [status, setStatus] = useState<"loading" | "error" | "success" | "pending" | "idle">("loading");
  const [errorMsg, setErrorMsg] = useState("");
  // 分享码输入
  const [shareCodeInput, setShareCodeInput] = useState("");
  const [isRedeeming, setIsRedeeming] = useState(false);

  const slotId = searchParams.get("slot_id");
  const parentId = searchParams.get("parent_id");
  const shareCodeParam = searchParams.get("code");

  useEffect(() => {
    // 如果URL参数中有分享码
    if (shareCodeParam && user) {
      redeemCode(shareCodeParam);
      return;
    }

    if (!slotId || !parentId) {
      // 没有URL参数，显示分享码输入表单
      if (!shareCodeParam) {
        setStatus("idle");
        return;
      }
      setStatus("error");
      setErrorMsg(t("invite.invalid_link") || "无效的邀请链接。Missing parameters.");
      return;
    }

    localStorage.setItem("invite_slot_id", slotId);
    localStorage.setItem("invite_parent_id", parentId);

    if (!user) {
      setStatus("pending");
      return;
    }

    const claim = async () => {
      try {
        await matchingService.claimHandover({ slotId, parentId });
        localStorage.removeItem("invite_slot_id");
        localStorage.removeItem("invite_parent_id");
        setStatus("success");
      } catch (err: any) {
        console.error(err);
        setStatus("error");
        setErrorMsg(err.message || t("invite.failed_to_claim") || "领取失败。Failed to claim.");
      }
    };

    claim();
  }, [slotId, parentId, user, shareCodeParam]);

  const redeemCode = async (code: string) => {
    setIsRedeeming(true);
    setStatus("loading");
    try {
      const result = await matchingService.redeemShareCode(code);
      if (result.success) {
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMsg(result.message || t("common.failed") || "兑换失败");
      }
    } catch (err: any) {
      setStatus("error");
      setErrorMsg(err.message || t("invite.network_error") || "网络错误");
    }
    setIsRedeeming(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <SeoHead
        path="invite"
        title="Invitation - BamaBang"
        description="Accept your BamaBang invitation and join the parent-led matchmaking platform."
      />
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {status === "idle" ? t("invite.enter_code") : t("invite.friend_pushed")}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {status === "idle" ? t("invite.enter_code_desc", { count: SHARE_CODE_LENGTH }) : t("invite.friend_shared_desc")}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
          {status === "loading" && <p>{t("common.loading") || "正在处理中 / Processing..."}</p>}
          {status === "error" && (
            <div className="text-red-600">
              <p className="font-bold">{t("invite.error_title") || "出错了 / Error"}</p>
              <p>{errorMsg}</p>
              <button 
                onClick={() => { setStatus("idle"); setErrorMsg(""); }}
                className="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                {t("common.retry") || "重试 / Try Again"}
              </button>
            </div>
          )}
          {status === "idle" && (
            <div>
              <p className="mb-6 text-gray-600">
                {t("invite.enter_code_desc", { count: SHARE_CODE_LENGTH })}
              </p>
              <input
                type="text"
                maxLength={SHARE_CODE_LENGTH}
                value={shareCodeInput}
                onChange={(e) => setShareCodeInput(e.target.value.toUpperCase())}
                placeholder={t("invite.placeholder", { count: SHARE_CODE_LENGTH })}
                className="w-full px-4 py-3 border border-gray-300 rounded-md text-center text-2xl tracking-widest font-bold focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <button
                onClick={() => {
                  if (!user) {
                    setStatus("pending");
                    localStorage.setItem("pending_share_code", shareCodeInput);
                    return;
                  }
                  redeemCode(shareCodeInput);
                }}
                disabled={shareCodeInput.length < SHARE_CODE_LENGTH || isRedeeming}
                className="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
              >
                {isRedeeming ? t("invite.redeeming") : t("invite.redeem_btn")}
              </button>
            </div>
          )}
          {status === "pending" && (
            <div>
              {WEB_AUTH_UI_ENABLED ? (
                <>
                  <p className="mb-6">{t("auth.login_register_to_view") || "请先登录或注册以查看该聊天对象。<br/>Please login or register to view the match."}</p>
                  <div className="space-y-4">
                    <Link
                      to="/login"
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                    >
                      {t("auth.login")}
                    </Link>
                    <Link
                      to="/register"
                      className="w-full flex justify-center py-2 px-4 border border-indigo-600 rounded-md shadow-sm text-sm font-medium text-indigo-600 bg-white hover:bg-gray-50 focus:outline-none"
                    >
                      {t("auth.register")}
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <p className="mb-6 text-gray-700">{t("invite.web_login_closed")}</p>
                  <Link
                    to="/download"
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                  >
                    {t("invite.go_download_app")}
                  </Link>
                </>
              )}
            </div>
          )}
          {status === "success" && (
            <div>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="mb-6 text-green-600 font-bold">{t("invite.success_title")}</p>
              <button
                onClick={() => navigate("/app/wallet")}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
              >
                {t("invite.enter_app")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvitePage;

